import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ErpModule } from '@/api/platform.api';
import { notificationsApi } from '@/api';
import type { PageResponse } from '@/types/api';
import type { NotificationResponse } from '@/types/notification';
import { queryKeys, tierOptions } from '@/lib/query';
import { useRealtimePushActive } from '@/hooks/useRealtimeSubscription';
import {
  notificationQueryKeys,
  optimisticClearAll,
  optimisticClearRead,
  optimisticMarkAllRead,
  optimisticMarkRead,
} from '@/utils/notificationCache';

type UseNotificationInboxOptions = {
  module?: ErpModule;
  page?: number;
  size?: number;
  /** Poll interval when push inactive — defaults to 30s. Pass 0 to disable. */
  pollMs?: number;
};

export function useNotificationListQuery(options: UseNotificationInboxOptions = {}) {
  const { module, page = 0, size = 10, pollMs: pollMsOverride } = options;
  const pushActive = useRealtimePushActive();
  const pollMs = pollMsOverride ?? (pushActive ? 0 : 30_000);
  const params = { page, size, module };

  return useQuery({
    queryKey: queryKeys.notifications.list(params),
    queryFn: () => notificationsApi.list(params),
    ...tierOptions('realtime', {
      refetchInterval: pollMs > 0 ? pollMs : false,
    }),
    retry: 1,
  });
}

export function useNotificationUnreadQuery(module?: ErpModule, pollMsOverride?: number) {
  const pushActive = useRealtimePushActive();
  const pollMs = pollMsOverride ?? (pushActive ? 0 : 30_000);

  return useQuery({
    queryKey: queryKeys.notifications.unread(module),
    queryFn: () => notificationsApi.unreadCount(module),
    ...tierOptions('realtime', {
      refetchInterval: pollMs > 0 ? pollMs : false,
    }),
    retry: 1,
  });
}

/** Combined inbox hook — replaces manual polling useEffect. */
export function useNotificationInboxQuery(options: UseNotificationInboxOptions = {}) {
  const { module, page = 0, size = 10, pollMs: pollMsOverride } = options;
  const pushActive = useRealtimePushActive();
  const pollMs = pollMsOverride ?? (pushActive ? 0 : 60_000);
  const qc = useQueryClient();
  const params = { page, size, module };
  const keys = notificationQueryKeys(params);

  const listQuery = useNotificationListQuery({ module, page, size, pollMs });
  const unreadQuery = useNotificationUnreadQuery(module, pollMs);

  const invalidateAll = async () => {
    await qc.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markRead = async (id: number) => {
    const prevList = qc.getQueryData(keys.list) as PageResponse<NotificationResponse> | undefined;
    const prevUnread = qc.getQueryData(keys.unread);
    qc.setQueryData(keys.list, optimisticMarkRead(prevList, id));
    qc.setQueryData(keys.unread, {
      count: Math.max(0, ((prevUnread as { count?: number } | undefined)?.count ?? 1) - 1),
    });
    try {
      await notificationsApi.markRead(id);
      await invalidateAll();
    } catch (err) {
      qc.setQueryData(keys.list, prevList);
      qc.setQueryData(keys.unread, prevUnread);
      throw err;
    }
  };

  const markAllRead = async () => {
    const prevList = qc.getQueryData(keys.list) as PageResponse<NotificationResponse> | undefined;
    const prevUnread = qc.getQueryData(keys.unread);
    qc.setQueryData(keys.list, optimisticMarkAllRead(prevList));
    qc.setQueryData(keys.unread, { count: 0 });
    try {
      await notificationsApi.markAllRead(module);
      await invalidateAll();
    } catch (err) {
      qc.setQueryData(keys.list, prevList);
      qc.setQueryData(keys.unread, prevUnread);
      throw err;
    }
  };

  const clearRead = async () => {
    const prevList = qc.getQueryData(keys.list) as PageResponse<NotificationResponse> | undefined;
    const prevUnread = qc.getQueryData(keys.unread);
    qc.setQueryData(keys.list, optimisticClearRead(prevList));
    try {
      await notificationsApi.clearRead(module);
      await invalidateAll();
    } catch (err) {
      qc.setQueryData(keys.list, prevList);
      qc.setQueryData(keys.unread, prevUnread);
      throw err;
    }
  };

  const clearAll = async () => {
    const prevList = qc.getQueryData(keys.list);
    const prevUnread = qc.getQueryData(keys.unread);
    qc.setQueryData(keys.list, optimisticClearAll());
    qc.setQueryData(keys.unread, { count: 0 });
    try {
      await notificationsApi.clearAll(module);
      await invalidateAll();
    } catch (err) {
      qc.setQueryData(keys.list, prevList);
      qc.setQueryData(keys.unread, prevUnread);
      throw err;
    }
  };

  const load = async () => {
    await Promise.all([listQuery.refetch(), unreadQuery.refetch()]);
  };

  const items = listQuery.data?.content ?? [];
  const readCount = items.filter((n) => n.readFlag === true).length;

  return {
    items,
    unread: unreadQuery.data?.count ?? 0,
    readCount,
    loading: listQuery.isLoading || unreadQuery.isLoading,
    totalElements: listQuery.data?.totalElements ?? 0,
    totalPages: listQuery.data?.totalPages ?? 0,
    load,
    markRead,
    markAllRead,
    clearRead,
    clearAll,
    isError: listQuery.isError || unreadQuery.isError,
  };
}
